import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ControlApiError, controlFetch } from "@/lib/api/client";
import { getControlPlaneRuntimeConfig } from "@/app/runtime-config";

type AuthStatus = "loading" | "authenticated" | "anonymous" | "provisioning" | "error";

export interface AuthUser {
  actorType: "operator_user";
  actorId: string;
  displayName: string;
  email?: string | null;
  preferredUsername?: string | null;
}

export interface AuthTenant {
  tenantId: string;
  tenantSlug?: string | null;
  displayName: string;
}

export interface AuthSession {
  user: AuthUser;
  tenant: AuthTenant;
  roles: string[];
  isPlatformAdmin: boolean;
  source: string;
  clientId?: string | null;
  authProvider?: string | null;
}

export interface AuthContextValue {
  status: AuthStatus;
  session: AuthSession | null;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  roles: string[];
  isPlatformAdmin: boolean;
  isConfigured: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (params?: { tenantId?: string; returnTo?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<AuthSession | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface ControlSessionResponse {
  user: {
    actorType: "operator_user";
    actorId: string;
    displayName: string;
    email?: string | null;
    preferredUsername?: string | null;
  };
  tenant: {
    tenantId: string;
    displayName: string;
    tenantSlug?: string | null;
  };
  roles: string[];
  isPlatformAdmin: boolean;
  source: string;
  clientId?: string | null;
  authProvider?: string | null;
}

async function loadSession() {
  const session = await controlFetch<ControlSessionResponse>("/api/session/me", {
    method: "GET",
  });
  return {
    user: {
      actorType: session.user.actorType,
      actorId: session.user.actorId,
      displayName: session.user.displayName,
      email: session.user.email ?? null,
      preferredUsername: session.user.preferredUsername ?? null,
    },
    tenant: {
      tenantId: session.tenant.tenantId,
      tenantSlug: session.tenant.tenantSlug ?? null,
      displayName: session.tenant.displayName,
    },
    roles: session.roles,
    isPlatformAdmin: session.isPlatformAdmin,
    source: session.source,
    clientId: session.clientId ?? null,
    authProvider: session.authProvider ?? null,
  } satisfies AuthSession;
}

function formatAuthError(error: unknown) {
  return error instanceof Error ? error.message : "Session bootstrap failed.";
}

function readApiErrorMessage(error: ControlApiError) {
  const body = error.body;
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    const directMessage = record.message;
    if (typeof directMessage === "string" && directMessage.trim()) {
      return directMessage;
    }
    const nested = record.error;
    if (nested && typeof nested === "object") {
      const nestedMessage = (nested as Record<string, unknown>).message;
      if (typeof nestedMessage === "string" && nestedMessage.trim()) {
        return nestedMessage;
      }
    }
  }
  return error.message;
}

function getCookieAuthBaseUrl() {
  const runtimeConfig = getControlPlaneRuntimeConfig();
  return runtimeConfig.apiBaseUrl?.trim() || window.location.origin;
}

function readLaunchTenantId() {
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant_id")?.trim() || params.get("tenantId")?.trim() || null;
}

function beginCookieLogin(params?: { tenantId?: string | null; returnTo?: string }) {
  const returnTo = params?.returnTo?.trim()
    ? new URL(params.returnTo, window.location.origin).toString()
    : window.location.href;
  const url = new URL("/auth/signin", getCookieAuthBaseUrl());
  url.searchParams.set("returnUrl", returnTo);
  if (params?.tenantId?.trim()) {
    url.searchParams.set("tenantId", params.tenantId.trim());
  }
  window.location.assign(url.toString());
}

function sameTenant(left: string, right: string) {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<AuthSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isConfigured = true;
  const refreshInFlightRef = useRef<Promise<AuthSession | null> | null>(null);
  const bootstrapStartedRef = useRef(false);

  const refreshSession = async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const refreshPromise = (async () => {
      try {
        const nextSession = await loadSession();
        if (!nextSession) {
          setStatus("anonymous");
          setSession(null);
          setError(null);
          return null;
        }
        const launchTenantId = readLaunchTenantId();
        if (launchTenantId && !sameTenant(nextSession.tenant.tenantId, launchTenantId)) {
          beginCookieLogin({ tenantId: launchTenantId });
          return null;
        }
        setStatus("authenticated");
        setSession(nextSession);
        setError(null);
        return nextSession;
      } catch (sessionError) {
        if (sessionError instanceof ControlApiError && sessionError.status === 401) {
          setStatus("anonymous");
          setSession(null);
          setError(null);
          const launchTenantId = readLaunchTenantId();
          if (launchTenantId) {
            beginCookieLogin({ tenantId: launchTenantId });
          }
          return null;
        }
        if (sessionError instanceof ControlApiError && sessionError.code === "tenant_provisioning_in_progress") {
          setStatus("provisioning");
          setSession(null);
          setError(readApiErrorMessage(sessionError));
          return null;
        }
        setStatus("error");
        setSession(null);
        setError(
          sessionError instanceof Error
            ? sessionError.message
            : "Session bootstrap failed.",
        );
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();
    refreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  };

  const bootstrapSession = useEffectEvent(async () => {
    await refreshSession();
  });

  useEffect(() => {
    if (bootstrapStartedRef.current) {
      return;
    }
    bootstrapStartedRef.current = true;
    void bootstrapSession();
  }, [bootstrapSession]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setSession(null);
      setStatus("anonymous");
      setError(null);
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized as EventListener);
    return () =>
      window.removeEventListener("auth:unauthorized", handleUnauthorized as EventListener);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const login = async (params?: { tenantId?: string; returnTo?: string }) => {
      setError(null);
      try {
        beginCookieLogin(params);
      } catch (authError) {
        setSession(null);
        setStatus("error");
        setError(formatAuthError(authError));
      }
    };

    const logout = async () => {
      setSession(null);
      setStatus("anonymous");
      setError(null);
      const url = new URL("/auth/signout", getCookieAuthBaseUrl());
      url.searchParams.set("returnUrl", window.location.origin);
      window.location.assign(url.toString());
    };

    return {
      status,
      session,
      user: session?.user ?? null,
      tenant: session?.tenant ?? null,
      roles: session?.roles ?? [],
      isPlatformAdmin: session?.isPlatformAdmin ?? false,
      isConfigured,
      isAuthenticated: status === "authenticated" && session !== null,
      error,
      login,
      logout,
      refreshSession,
    };
  }, [error, isConfigured, session, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
